import { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Download, CheckCircle2, Trash2, Receipt as ReceiptIcon, FileText, AlertTriangle, Pencil, CreditCard, Copy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const STATUSES = [
  { value: "all", label: "All" },
  { value: "unpaid", label: "Unpaid" },
  { value: "invoice_sent", label: "Invoice Sent" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "refunded", label: "Refunded" },
];

const statusBadge = {
  unpaid: "bg-slate-100 text-slate-700 border-slate-200",
  invoice_sent: "bg-blue-100 text-blue-700 border-blue-200",
  overdue: "bg-red-100 text-red-700 border-red-200",
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  refunded: "bg-amber-100 text-amber-700 border-amber-200",
};

const statusLabel = { unpaid: "Unpaid", invoice_sent: "Invoice Sent", overdue: "Overdue", paid: "Paid", refunded: "Refunded" };

export default function PaymentsPage() {
  useDocumentTitle("Payments & Invoices", "Invoices, receipts and Stripe payment links.");
  const { user } = useAuth();
  const canEdit = ["admin", "superadmin"].includes(user?.role);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [members, setMembers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [form, setForm] = useState({ member_id: "", course_id: "", amount_net: 0, vat_applicable: false, due_date: "", notes: "" });
  const [editForm, setEditForm] = useState({ status: "", amount_net: 0, vat_applicable: false, payment_method: "", due_date: "", notes: "" });
  const [busy, setBusy] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(true);

  useEffect(() => {
    api.get("/settings/stripe")
      .then(({ data }) => setStripeEnabled(data?.enabled !== false))
      .catch(() => { /* non-fatal — default to on so buttons still render */ });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [{ data }, { data: s }] = await Promise.all([
        api.get(filter === "all" ? "/payments" : `/payments?status=${filter}`),
        api.get("/payments/summary"),
      ]);
      setPayments(data.payments || []);
      setSummary(s);
    } catch (err) { if (!isAuthError(err)) toast.error("Failed to load payments"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = async () => {
    try {
      const [{ data: m }, { data: c }] = await Promise.all([
        api.get("/members?limit=10000&role=student"),
        api.get("/courses?limit=200"),
      ]);
      setMembers(m.members || []);
      setCourses(c.courses || []);
    } catch (err) {
      console.error("Failed to load members/courses for payment dialog:", err);
      toast.error("Couldn't load members or courses. Please refresh and try again.");
    }
    setForm({ member_id: "", course_id: "", amount_net: 0, vat_applicable: false, due_date: "", notes: "" });
    setCreateOpen(true);
  };

  const handleCourseSelect = (courseId) => {
    const c = courses.find(x => x.id === courseId);
    if (c) {
      setForm(f => ({ ...f, course_id: courseId, amount_net: c.price_per_student || 0, vat_applicable: !!c.vat_applicable }));
    } else {
      setForm(f => ({ ...f, course_id: "" }));
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.member_id) { toast.error("Select a member"); return; }
    try {
      const body = { ...form, course_id: form.course_id || "", amount_net: parseFloat(form.amount_net) || 0 };
      await api.post("/payments", body);
      toast.success("Payment record created");
      setCreateOpen(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      status: p.status,
      amount_net: p.amount_net,
      vat_applicable: !!p.vat_applicable,
      payment_method: p.payment_method || "",
      due_date: p.due_date || "",
      notes: p.notes || "",
    });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const body = { ...editForm, amount_net: parseFloat(editForm.amount_net) || 0 };
      await api.put(`/payments/${editing.id}`, body);
      toast.success("Payment updated");
      setEditOpen(false);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markPaid = async (p) => {
    setBusy(p.id);
    try {
      await api.put(`/payments/${p.id}`, { status: "paid", payment_method: p.payment_method || "Bank Transfer" });
      toast.success(`Marked ${p.invoice_number} as paid`);
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const downloadPdf = async (p, kind) => {
    setBusy(p.id);
    try {
      const res = await api.get(`/payments/${p.id}/pdf?kind=${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${kind === "receipt" ? p.receipt_number : p.invoice_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { toast.error("Failed to generate PDF"); }
    finally { setBusy(null); }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`Delete payment ${p.invoice_number}?`)) return;
    try {
      await api.delete(`/payments/${p.id}`);
      toast.success("Payment deleted");
      fetchData();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const generatePayLink = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post(`/payments/${p.id}/checkout-session`, { origin_url: window.location.origin });
      // Copy link to clipboard (best-effort — non-fatal if clipboard API is unavailable)
      try {
        await navigator.clipboard.writeText(data.url);
      } catch (clipErr) {
        if (process.env.NODE_ENV !== "production") console.warn("Clipboard write failed:", clipErr);
      }
      // Open in new tab
      window.open(data.url, "_blank", "noopener,noreferrer");
      toast.success("Pay link created — opened in new tab and copied to clipboard");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't create pay link");
    } finally {
      setBusy(null);
    }
  };

  const copyPayLink = async (p) => {
    if (!p.stripe_checkout_url) return;
    try {
      await navigator.clipboard.writeText(p.stripe_checkout_url);
      toast.success("Pay link copied to clipboard");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const filtered = payments.filter(p => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.member_name || "").toLowerCase().includes(s)
      || (p.course_title || "").toLowerCase().includes(s)
      || (p.invoice_number || "").toLowerCase().includes(s)
      || (p.receipt_number || "").toLowerCase().includes(s);
  });

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading payments...</div>;

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard label="Paid" value={summary?.paid_total} count={(summary?.paid?.count || 0) + (summary?.part_paid?.count || 0)} color="emerald" testId="summary-paid" />
        <SummaryCard label="Outstanding" value={summary?.outstanding_total} count={(summary?.unpaid?.count || 0) + (summary?.invoice_sent?.count || 0)} color="amber" testId="summary-outstanding" />
        <SummaryCard label="Overdue" value={summary?.overdue?.total} count={summary?.overdue?.count} color="red" icon={<AlertTriangle size={14} />} testId="summary-overdue" />
        <SummaryCard label="Refunded" value={summary?.refunded?.total} count={summary?.refunded?.count} color="slate" testId="summary-refunded" />
      </div>

      <div className="bg-white rounded-lg border border-border">
        <div className="p-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <ReceiptIcon size={18} className="text-slate-600" />
            <h2 className="font-heading font-semibold text-base">Payments & Invoices</h2>
            <Badge variant="outline" className="text-[10px]">{filtered.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Input placeholder="Search invoice / member..." value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 w-56" data-testid="payments-search" />
            {canEdit && (
              <Button size="sm" onClick={openCreate} data-testid="add-payment-btn">
                <Plus size={14} className="mr-1" /> New Payment
              </Button>
            )}
          </div>
        </div>

        <div className="px-4 pt-3">
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList className="h-8">
              {STATUSES.map(s => <TabsTrigger key={s.value} value={s.value} className="text-xs h-7" data-testid={`filter-${s.value}`}>{s.label}</TabsTrigger>)}
            </TabsList>
          </Tabs>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left px-4 py-2.5 font-medium">Invoice / Receipt</th>
                <th className="text-left px-4 py-2.5 font-medium">Member</th>
                <th className="text-left px-4 py-2.5 font-medium">Course</th>
                <th className="text-right px-4 py-2.5 font-medium">Net</th>
                <th className="text-right px-4 py-2.5 font-medium">VAT</th>
                <th className="text-right px-4 py-2.5 font-medium">Total</th>
                <th className="text-center px-4 py-2.5 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 font-medium">Due / Paid</th>
                <th className="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">
                    <div className="font-semibold text-foreground">{p.invoice_number}</div>
                    {p.receipt_number && <div className="text-emerald-600">{p.receipt_number}</div>}
                    {p.quote_code && <div className="text-[10px] text-sky-700" title="Managed from the learner's quote" data-testid={`payment-quote-${p.id}`}>Quote {p.quote_code}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{p.member_name}</div>
                    <div className="text-xs text-muted-foreground">{p.member_email}</div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{p.course_title || "—"}</td>
                  <td className="px-4 py-3 text-right">£{Number(p.amount_net).toFixed(2)}</td>
                  <td className="px-4 py-3 text-right">{p.vat_applicable ? `£${Number(p.vat_amount).toFixed(2)}` : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 text-right font-semibold">£{Number(p.total).toFixed(2)}</td>
                  <td className="px-4 py-3 text-center">
                    <Badge variant="outline" className={`text-[10px] ${statusBadge[p.status] || ""}`} data-testid={`status-${p.id}`}>{statusLabel[p.status] || p.status}</Badge>
                    {p.status !== "paid" && p.amount_paid > 0 && <div className="text-[10px] text-amber-700 mt-1" data-testid={`part-paid-${p.id}`}>£{Number(p.amount_paid).toFixed(2)} paid · £{Number(p.balance).toFixed(2)} due</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {p.status === "paid"
                      ? <span>Paid {p.paid_date}</span>
                      : (p.due_date ? <span>Due {p.due_date}</span> : "—")}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.status !== "paid" && p.status !== "refunded" && canEdit && !p.quote_id && (
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 hover:text-emerald-800" disabled={busy === p.id} onClick={() => markPaid(p)} title="Mark Paid" data-testid={`mark-paid-${p.id}`}>
                          <CheckCircle2 size={14} />
                        </Button>
                      )}
                      {p.status === "paid" && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy === p.id} onClick={() => downloadPdf(p, "receipt")} title="Download Receipt" data-testid={`download-receipt-${p.id}`}>
                            <Download size={14} />
                          </Button>
                        </>
                      )}
                      {p.status !== "paid" && (
                        <>
                          {stripeEnabled && (
                            <>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-indigo-700 hover:text-indigo-800" disabled={busy === p.id} onClick={() => generatePayLink(p)} title="Get Stripe Pay Link" data-testid={`pay-link-${p.id}`}>
                                <CreditCard size={14} />
                              </Button>
                              {p.stripe_checkout_url && (
                                <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copyPayLink(p)} title="Copy pay link" data-testid={`copy-pay-link-${p.id}`}>
                                  <Copy size={13} />
                                </Button>
                              )}
                            </>
                          )}
                          <Button size="sm" variant="ghost" className="h-7 px-2" disabled={busy === p.id} onClick={() => downloadPdf(p, "invoice")} title="Download Invoice" data-testid={`download-invoice-${p.id}`}>
                            <FileText size={14} />
                          </Button>
                        </>
                      )}
                      {canEdit && !p.quote_id && (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openEdit(p)} title="Edit" data-testid={`edit-payment-${p.id}`}>
                            <Pencil size={13} />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" onClick={() => deletePayment(p)} title="Delete" data-testid={`delete-payment-${p.id}`}>
                            <Trash2 size={13} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div className="p-12 text-center text-muted-foreground text-sm">No payments found{filter !== "all" ? ` with status "${filter}"` : ""}.</div>}
        </div>
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Payment Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-3">
            <div>
              <Label className="text-xs">Member</Label>
              <SearchableSelect
                options={members.map(m => ({ value: m.id, label: m.name, hint: m.email || "(walk-in)" }))}
                value={form.member_id}
                onChange={(v) => setForm(f => ({ ...f, member_id: v }))}
                placeholder="Select member..."
                searchPlaceholder="Search by name or email…"
                testId="payment-member-select"
              />
            </div>
            <div>
              <Label className="text-xs">Course (optional)</Label>
              <SearchableSelect
                options={courses.map(c => ({ value: c.id, label: c.title, hint: `£${c.price_per_student || 0}` }))}
                value={form.course_id || ""}
                onChange={(v) => handleCourseSelect(v)}
                placeholder="No course"
                searchPlaceholder="Search courses…"
                testId="payment-course-select"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (Net £)</Label>
                <Input type="number" step="0.01" min="0" value={form.amount_net} onChange={(e) => setForm(f => ({ ...f, amount_net: e.target.value }))} required data-testid="payment-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center justify-between border border-border rounded p-2">
              <div>
                <Label className="text-xs">VAT Applicable (20%)</Label>
                <p className="text-[11px] text-muted-foreground">Total: £{((parseFloat(form.amount_net) || 0) * (form.vat_applicable ? 1.2 : 1)).toFixed(2)}</p>
              </div>
              <Switch checked={form.vat_applicable} onCheckedChange={(v) => setForm(f => ({ ...f, vat_applicable: v }))} data-testid="payment-vat-switch" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="submit-payment-btn">Create</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment {editing?.invoice_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div>
              <Label className="text-xs">Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger className="h-9" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="invoice_sent">Invoice Sent</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Amount (Net £)</Label>
                <Input type="number" step="0.01" min="0" value={editForm.amount_net} onChange={(e) => setEditForm(f => ({ ...f, amount_net: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" value={editForm.due_date} onChange={(e) => setEditForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Payment Method</Label>
              <Input value={editForm.payment_method} onChange={(e) => setEditForm(f => ({ ...f, payment_method: e.target.value }))} placeholder="Bank Transfer / Card / Cash" />
            </div>
            <div className="flex items-center justify-between border border-border rounded p-2">
              <Label className="text-xs">VAT Applicable</Label>
              <Switch checked={editForm.vat_applicable} onCheckedChange={(v) => setEditForm(f => ({ ...f, vat_applicable: v }))} />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={editForm.notes} onChange={(e) => setEditForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, count, color, icon, testId }) {
  const colorMap = {
    emerald: "text-emerald-700",
    amber: "text-amber-700",
    red: "text-red-700",
    slate: "text-slate-700",
  };
  return (
    <div className="bg-white rounded-lg border border-border p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        {icon}
      </div>
      <p className={`text-2xl font-bold ${colorMap[color]}`}>£{(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{count || 0} {count === 1 ? "record" : "records"}</p>
    </div>
  );
}
